import {
  CardContent,
  List,
  ListItem,
  ListSubheader,
  TextField,
} from "@mui/material"
import { useRouter } from "next/router"
import React, { FC } from "react"

import { Card } from "src/components/common/card"
import { create } from "src/helpers/bem"

import { useTranslation } from "src/hooks/translation"

import { CCInformation } from "./ShopCartOverview"
import styles from "./ShopCartOverviewBillingOptionsCard.module.scss"

const bem = create(styles, "ShopCartOverviewBillingOptionsCard")

export type ShopCartOverviewCCInformationCardProps = {
  updateCCInformation: (ccInfo: CCInformation) => void
  ccInformation: CCInformation
  needsCCInformation?: boolean
}

export const ShopCartOverviewCCInformationCard: FC<
  ShopCartOverviewCCInformationCardProps
> = ({ updateCCInformation, ccInformation, needsCCInformation }) => {
  const translation = useTranslation()
  const router = useRouter()
  const m = translation.messages.pages.shop.cart.checkout

  const isValidCreditCardNumber = (number: string) => {
    // Remove spaces
    const num = number.replace(/\s+/g, "")
    // Luhn algorithm
    let sum = 0
    let shouldDouble = false
    for (let i = num.length - 1; i >= 0; i--) {
      let digit = parseInt(num.charAt(i), 10)
      if (shouldDouble) {
        digit *= 2
        if (digit > 9) digit -= 9
      }
      sum += digit
      shouldDouble = !shouldDouble
    }
    return num.length >= 16 && num.length <= 19 && sum % 10 === 0
  }

  const isValidExpirationDate = (value: string) => {
    // Format MM/YY
    if (!/^\d{2}\/\d{2}$/.test(value)) return false
    const [monthStr, yearStr] = value.split("/")
    const month = parseInt(monthStr, 10)
    const year = parseInt(yearStr, 10)
    if (month < 1 || month > 12) return false

    // Expiry is end of month
    const now = new Date()
    const currentYear = now.getFullYear() % 100 // get last two digits
    const currentMonth = now.getMonth() + 1 // 0-based

    if (year < currentYear) return false
    if (year === currentYear && month < currentMonth) return false

    return true
  }

  const isValidCVV = (cvv: string) => {
    // CVV is typically 3 or 4 digits
    return /^\d{3,4}$/.test(cvv)
  }

  const renderCCInformation = () => (
    <List subheader={<ListSubheader>{m.ccInformation.title}</ListSubheader>}>
      <ListItem sx={{ display: "flex", gap: "15px" }}>
        <TextField
          fullWidth
          label={m.ccInformation.name.label}
          required={needsCCInformation}
          value={ccInformation.name}
          onChange={(e) =>
            updateCCInformation({ ...ccInformation, name: e.target.value })
          }
        />
      </ListItem>
      <ListItem sx={{ display: "flex", gap: "15px" }}>
        <TextField
          fullWidth
          inputProps={{ maxLength: 19 }} // Format: XXXX XXXX XXXX XXXX
          label={m.ccInformation.number.label}
          required={needsCCInformation}
          value={ccInformation.number}
          error={
            !!ccInformation.number &&
            !isValidCreditCardNumber(ccInformation.number)
          }
          helperText={
            !!ccInformation.number &&
            !isValidCreditCardNumber(ccInformation.number)
              ? m.ccInformation.validation.number
              : undefined
          }
          onChange={(e) => {
            // Remove all non-digits
            let val = e.target.value.replace(/\D/g, "")
            // Insert a space every 4 digits
            val = val.replace(/(.{4})/g, "$1 ").trim()
            updateCCInformation({ ...ccInformation, number: val })
          }}
        />
      </ListItem>
      <ListItem sx={{ display: "flex", gap: "15px" }}>
        <ListItem sx={{ display: "flex", gap: "15px" }}>
          <TextField
            fullWidth
            inputProps={{ maxLength: 5 }} // Format: MM/YY
            label={m.ccInformation.expirationDate.label}
            required={needsCCInformation}
            value={ccInformation.expirationDate}
            error={
              !!ccInformation.expirationDate &&
              !isValidExpirationDate(ccInformation.expirationDate)
            }
            helperText={
              !!ccInformation.expirationDate &&
              !isValidExpirationDate(ccInformation.expirationDate)
                ? m.ccInformation.validation.expirationDate
                : undefined
            }
            onChange={(e) => {
              let val = e.target.value.replace(/[^\d]/g, "") // Remove non-digits
              if (val.length > 2) {
                val = val.slice(0, 2) + "/" + val.slice(2, 4)
              }
              updateCCInformation({ ...ccInformation, expirationDate: val })
            }}
          />
        </ListItem>
      </ListItem>
      <ListItem sx={{ display: "flex", gap: "15px" }}>
        <TextField
          fullWidth
          error={!!ccInformation.cvv && !isValidCVV(ccInformation.cvv)}
          inputProps={{ maxLength: 4 }}
          label={m.ccInformation.cvv.label}
          required={needsCCInformation}
          value={ccInformation.cvv}
          helperText={
            !!ccInformation.cvv && !isValidCVV(ccInformation.cvv)
              ? m.ccInformation.validation.cvv || "Ungültiger CVV"
              : undefined
          }
          onChange={(e) =>
            updateCCInformation({
              ...ccInformation,
              cvv: e.target.value.replace(/\D/g, ""), // Only allow numbers
            })
          }
        />
      </ListItem>
    </List>
  )

  return (
    <Card>
      <CardContent>
        <div className={bem()}>{renderCCInformation()}</div>
      </CardContent>
    </Card>
  )
}
